<script setup>
/**
 * Mudeli kvaliteedi mõõdikud — Model Quality Metrics tab content.
 *
 * Powered by GET /v2/models/{id}/quality-metrics from the REdata API. Three
 * subscription-tier-aware sections per Jaan Olev's RED-196 framing:
 *
 *   Tier 1 (everyone)     — confidence gauge + grade + verdict
 *   Tier 2 (paid)         — IAAO breakdown + uncertainty stats (accordion)
 *   Tier 3 (internal)     — Spetsialisti vaade: methodology, top features
 *
 * Visibility is driven by Inertia-shared `auth.can` flags
 * (`view-advanced-model-metrics`, `view-internal-model-metrics`).
 *
 * RED-363: every number here is MEASURED — the model's accuracy in the
 * valuation's sealed segment (`modelQualityMetrics.sealed`); the in-sample
 * trust score, grade, "95 % CI" rows and error-model MAE are not shown.
 *
 * RED-356 (Jaan Olev, 13.10): no hit rate in words — the 1–10 gauge ("N juhul
 * 10-st" within ±20 %) and the ±X % chip's "N % müükidest jäi sisse" coverage
 * rows are gone. The share within ±20 % stays as a plain metric; the ±10 %
 * row is gone until it has a real product target (Jaan 28.10).
 *
 * RED-355 B7: the server's `evidence` (ModelQualityEvidence, the same rule the
 * lühiaruanne applies) picks one of two states. `measured` (basis
 * `out_of_time` or `as_served`): the numbers, under the sentence naming the
 * basis in words, n and the period. `pending` (`selection_used`, `in_sample`,
 * no dossier): the B6 line "Sõltumatu kontroll ootab (…)" and no number.
 *
 * RED-356 (Toomas 14.10 + 16.10, Rasmus 16.10): the grade comes back,
 * recomputed on the server from the sealed measurement only
 * (ModelQualityGrade, the rule the Word codes ${mudel-grade},
 * ${mudel-trust-score}, ${mudel-verdict} and ${mudel-confidence-level} print):
 * the "Usaldusväärsus N / 10" gauge and the letter. The headline row is
 * Jaan's four (±20 % share, median ratio, COD, PRD), each with its target
 * (IAAO's bands; ≥ 95 % for the ±20 % share since Toomas 26.10) and the
 * explanation the report's ${kvaliteedimoodikud} table prints. The dossier's
 * own in-sample `grade` / `verdict` / `trust_score_components` are never read.
 *
 * RED-356 (Toomas 19.10): the screen drops the verdict line for every grade (it
 * stays in ${mudel-verdict}) and the "Hinde komponendid" block (it repeated the
 * headline rows). Every number in "Detailne ülevaade" is also its own Word
 * code, named next to it (`data-word-code`) and filled from the same field
 * (QuickValuationExportService::fillModelQualityMetrics()).
 *
 * RED-356 (Jaan Olev, 28.10): no label says IAAO (the plain names the headline
 * cards use, et.json `metrics.<key>.label`); the measured sentence, with n, the
 * period and how many sales the trimming left out, is back under the numbers
 * (${kvaliteedimoodikud} still leaves it out, Toomas 26.10); and
 * "Detailne ülevaade" lists only the numbers the independent check carries
 * (`details`, ModelQualityGrade::details()): a number it lacks has no row,
 * never a dash, and R², MAE and RMSE never come from the training-time test split.
 */
import { computed } from 'vue'
import { usePage } from '@inertiajs/vue3'
import Accordion from 'primevue/accordion'
import AccordionPanel from 'primevue/accordionpanel'
import AccordionHeader from 'primevue/accordionheader'
import AccordionContent from 'primevue/accordioncontent'

const props = defineProps({
  /**
   * Full payload from /v2/models/{id}/quality-metrics plus the server's
   * `evidence` {state, basis, sentence}, `grade` {score, letter, level,
   * components} (null without evidence), `headlines` [{key, value, target,
   * is_good}] and `details` [{key, code, value}]. Required and non-null — the
   * parent (QuickValuationResult.vue) mounts this only once `evidence` arrived.
   */
  modelQualityMetrics: {
    type: Object,
    required: true,
  },
})

const can = computed(() => usePage().props?.auth?.can ?? {})
const canAdvanced = computed(() => !!can.value['view-advanced-model-metrics'])
const canInternal = computed(() => !!can.value['view-internal-model-metrics'])

const tier3 = computed(() => props.modelQualityMetrics.tier3_overview ?? null)

// `measured` shows the numbers with the sentence naming n, the period and the
// trimming; `pending` only the line "Sõltumatu kontroll ootab (tehingud pärast
// 28.08.2026)." — both worded once, in PHP (ModelQualityEvidence).
const evidence = computed(() => props.modelQualityMetrics.evidence ?? {})
const isMeasured = computed(() => evidence.value.state === 'measured')

// Server-computed from the sealed measurement (ModelQualityGrade); null / empty with the pending line.
const grade = computed(() => (isMeasured.value ? props.modelQualityMetrics.grade ?? null : null))
const headlines = computed(() => (isMeasured.value ? props.modelQualityMetrics.headlines ?? [] : []))
// "Detailne ülevaade": each number of the independent check with the Word code
// that prints the same field (RED-356, Toomas 19.10); none the seal lacks (Jaan 28.10).
const detailMetrics = computed(() => (isMeasured.value ? props.modelQualityMetrics.details ?? [] : []))

// The old grade palette (redata-api _GRADE_COLOR_HEX), as Tailwind classes.
const GRADE_TEXT_CLASS = {
  A: 'text-emerald-500',
  B: 'text-lime-500',
  C: 'text-amber-500',
  D: 'text-red-500',
  F: 'text-red-800',
}

// 1–10 gauge: ten ticks, the first `level` filled red → amber → green
// (the pre-RED-363 "Usaldusväärsus" gauge, now on the sealed score).
const ticks = computed(() => {
  const level = grade.value?.level ?? 0
  return Array.from({ length: 10 }, (_, i) => {
    const position = i + 1
    const color = position <= 3 ? 'bg-red-500' : position <= 6 ? 'bg-amber-500' : 'bg-emerald-500'
    return { position, colorClass: position <= level ? color : 'bg-slate-200' }
  })
})

const PERCENT_METRICS = new Set(['within_20', 'cod'])
const EURO_METRICS = new Set(['mae_eur', 'rmse_eur'])

// Every caller passes a number: a metric without one has no row (Jaan 28.10).
function formatMetric(key, value) {
  if (PERCENT_METRICS.has(key)) return formatPct(value)
  if (EURO_METRICS.has(key)) return formatEur(value)
  return formatFloat(value)
}

function formatPct(v, decimals = 1) {
  return `${Number(v).toFixed(decimals).replace('.', ',')}\u00a0%`
}
function formatEur(v) {
  return `${Math.round(Number(v)).toLocaleString('et-EE')}\u00a0€`
}
function formatFloat(v, decimals = 3) {
  return Number(v).toFixed(decimals).replace('.', ',')
}
</script>

<template>
  <div id="model-quality" class="bg-white rounded-2xl shadow-xs border border-slate-100 p-6 mt-6" data-testid="qv-result-model-quality">
    <!-- RED-356 (Toomas 16.10): no technical model id in the header, for anyone.
         Admins still see the run id under "Spetsialisti vaade" (Tier 3). -->
    <div data-testid="qv-model-quality-header" class="flex items-center gap-2 mb-1">
      <span class="pi pi-shield text-slate-500"></span>
      <h3 class="font-medium text-slate-800">Mudeli kvaliteet</h3>
    </div>
    <!-- RED-355 B6: no evidence yet → this one line, no number. -->
    <p v-if="!isMeasured" data-testid="qv-model-quality-pending" class="text-sm text-slate-500">
      {{ evidence.sentence }}
    </p>

    <!-- ───────── Tier 1: gauge, grade, headline metrics (everyone) ───────── -->
    <div v-if="isMeasured" class="mt-4">
      <div v-if="grade" class="grid grid-cols-1 md:grid-cols-3 gap-4 items-center mb-2" data-testid="qv-model-quality-grade">
        <!-- Gauge: the grade's 1–10 level -->
        <div class="md:col-span-2" data-testid="qv-model-quality-gauge">
          <div class="text-xs uppercase tracking-wide text-slate-400 mb-2">
            {{ $t('messages.quickValuation.modelQuality.gaugeLabel') }}
          </div>
          <div class="flex items-center gap-1 mb-2">
            <div
              v-for="tick in ticks"
              :key="tick.position"
              :class="['h-3 flex-1 rounded-xs transition-colors', tick.colorClass]"
              :title="`${tick.position}/10`"
            />
          </div>
          <div class="flex items-baseline gap-2">
            <span class="text-3xl font-semibold text-slate-800" data-testid="qv-model-quality-level">
              {{ grade.level }}
            </span>
            <span class="text-sm text-slate-500">{{ $t('messages.quickValuation.modelQuality.gaugeUnit') }}</span>
          </div>
        </div>

        <!-- Grade card -->
        <div class="rounded-xl border border-slate-100 px-4 py-3 text-center">
          <div class="text-xs uppercase tracking-wide text-slate-400 mb-1">
            {{ $t('messages.quickValuation.modelQuality.gradeLabel') }}
          </div>
          <div
            :class="['text-4xl font-semibold', GRADE_TEXT_CLASS[grade.letter] ?? 'text-slate-400']"
            data-testid="qv-model-quality-letter"
          >
            {{ grade.letter }}
          </div>
        </div>
      </div>

      <!-- Headline metrics: Jaan's four, each with its target and a met / missed
           icon (ModelQualityGrade::target(), the Word table's "Sihtväärtus"). -->
      <div
        v-if="headlines.length"
        class="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4"
        data-testid="qv-model-quality-headlines"
      >
        <div
          v-for="metric in headlines"
          :key="metric.key"
          class="rounded-lg border border-slate-100 p-3"
          data-testid="qv-model-quality-headline"
          :data-metric-key="metric.key"
        >
          <div class="text-xs text-slate-500 mb-1">
            {{ $t(`messages.quickValuation.modelQuality.metrics.${metric.key}.label`) }}
          </div>
          <div class="flex items-center gap-1.5">
            <span class="text-lg font-medium text-slate-800">
              {{ formatMetric(metric.key, metric.value) }}
            </span>
            <span
              v-if="metric.target"
              :class="[
                'pi',
                metric.is_good ? 'pi-check-circle text-emerald-500' : 'pi-exclamation-circle text-amber-500',
              ]"
              :title="
                metric.is_good
                  ? $t('messages.quickValuation.modelQuality.targetMet')
                  : $t('messages.quickValuation.modelQuality.target', { target: metric.target })
              "
            />
          </div>
          <div v-if="metric.target" class="text-xs text-slate-400 mt-0.5">
            {{ $t('messages.quickValuation.modelQuality.target', { target: metric.target }) }}
          </div>
          <p class="text-xs text-slate-500 mt-1.5">
            {{ $t(`messages.quickValuation.modelQuality.metrics.${metric.key}.explanation`) }}
          </p>
        </div>
      </div>

      <!-- RED-356 (Jaan 28.10): what the numbers were measured on — n, the period
           and the trimming — right under them. -->
      <p v-if="evidence.sentence" data-testid="qv-model-quality-measured" class="text-xs text-slate-500 mt-3">
        {{ evidence.sentence }}
      </p>

      <!-- ───────── Tier 2: advanced view (paid users) ───────── -->
      <Accordion v-if="canAdvanced && detailMetrics.length" :value="null" class="mt-6">
        <AccordionPanel value="advanced">
          <AccordionHeader>
            <span class="font-medium text-slate-700">Detailne ülevaade</span>
          </AccordionHeader>
          <AccordionContent>
            <div class="grid grid-cols-2 md:grid-cols-3 gap-4 mt-2 text-sm">
              <div
                v-for="metric in detailMetrics"
                :key="metric.code"
                data-testid="qv-model-quality-detail"
                :data-word-code="metric.code"
              >
                <div class="text-xs text-slate-400">
                  {{ $t(`messages.quickValuation.modelQuality.metrics.${metric.key}.label`) }}
                </div>
                <div class="font-medium text-slate-800">{{ formatMetric(metric.key, metric.value) }}</div>
              </div>
            </div>
          </AccordionContent>
        </AccordionPanel>
      </Accordion>

      <!-- ───────── Tier 3: Spetsialisti vaade (internal/admin only) ───────── -->
      <Accordion v-if="canInternal && tier3 && Object.keys(tier3).length > 0" :value="null" class="mt-3">
        <AccordionPanel value="specialist">
          <AccordionHeader>
            <span class="font-medium text-slate-700">Spetsialisti vaade</span>
            <span class="text-xs text-amber-600 ml-2">(sisemine)</span>
          </AccordionHeader>
          <AccordionContent>
            <div v-if="tier3.identity?.run_id" class="mb-3 text-sm">
              <div class="text-xs text-slate-400">Mudel</div>
              <div class="font-mono text-xs text-slate-700">
                {{ tier3.identity.run_id }}
              </div>
            </div>

            <div v-if="tier3.methodology?.target_transform || tier3.identity?.n_features" class="mb-3 text-sm">
              <div class="text-xs text-slate-400 mb-1">Metoodika</div>
              <div class="text-slate-600">
                <span v-if="tier3.methodology?.target_transform" class="mr-3">
                  Target: <code class="text-xs">{{ tier3.methodology.target_transform }}</code>
                </span>
                <span v-if="tier3.identity?.n_features">Tunnuseid: {{ tier3.identity.n_features }}</span>
              </div>
            </div>

            <div v-if="tier3.top_30_features?.length" class="mb-3">
              <div class="text-xs text-slate-400 mb-1">
                Tähtsaimad tunnused (top {{ tier3.top_30_features.length }})
              </div>
              <ul class="text-xs text-slate-600 space-y-0.5 max-h-48 overflow-y-auto">
                <li
                  v-for="feature in tier3.top_30_features"
                  :key="feature.feature"
                  class="flex justify-between font-mono"
                >
                  <span>{{ feature.feature }}</span>
                  <span v-if="Number.isFinite(feature.importance)" class="text-slate-400 ml-3">
                    {{ formatFloat(feature.importance) }}
                  </span>
                </li>
              </ul>
            </div>

            <div v-if="tier3.known_limitations" class="text-xs text-slate-500 italic mt-3">
              {{ tier3.known_limitations }}
            </div>
          </AccordionContent>
        </AccordionPanel>
      </Accordion>
    </div>
  </div>
</template>
